import { LANGUAGE_DISPLAY_NAMES, type SupportedLanguage } from '@shared/core/localized-defaults'
import classNames from 'classnames'
import { Info } from 'lucide-react'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Button, FormattedText, Typography } from 'src/modules/core/components'
import { formatCountryName } from 'src/modules/core/utils/country.utils'
import { MESSAGE_LANGUAGE_ANCHOR_ID } from './client-message-language-card.domain'
import type {
  ClientMessageLanguageCardProps,
  MessageLanguageDetailsProps,
  MessageLanguageFooterProps,
  StagedChangeProps,
} from './client-message-language-card.interface'
import classes from './client-message-language-card.module.scss'
import { MessageLanguagePreview } from './message-language-preview'
import { MessageLanguageSelect } from './message-language-select'
import { useClientMessageLanguageCard } from './use-client-message-language-card'

const englishName = (language: SupportedLanguage) => LANGUAGE_DISPLAY_NAMES[language].english

function MessageLanguageDetails(props: MessageLanguageDetailsProps) {
  const { saved, selected, isStaged, automaticLanguage, automaticSourceText } = props
  const { formatMessage } = useIntl()
  const automaticLabel = formatMessage(
    { id: 'settings.messageLanguage.automatic' },
    { language: englishName(automaticLanguage) },
  )
  return (
    <div className={classes.details} data-testid="message-language-details">
      {isStaged && (
        <Typography variant="body" className={classes.status}>
          <FormattedText
            id="settings.messageLanguage.currently"
            values={{ value: saved === null ? automaticLabel : englishName(saved) }}
          />
        </Typography>
      )}
      {selected === null && <Typography variant="body">{automaticSourceText}</Typography>}
      <Typography variant="body" className={classes.reach}>
        <FormattedText
          id={selected === null ? 'settings.messageLanguage.automaticReach' : 'settings.messageLanguage.chosenReach'}
          values={{ language: englishName(selected ?? automaticLanguage) }}
        />
      </Typography>
    </div>
  )
}

function StagedChange(props: StagedChangeProps) {
  return (
    <>
      <TemplatesWarning language={props.language} />
      <MessageLanguagePreview {...props} />
    </>
  )
}

function TemplatesWarning({ language }: { language: SupportedLanguage }) {
  return (
    <div className={classes.warning} role="note">
      <Info className={classes.warningIcon} size={20} aria-hidden="true" />
      <Typography variant="body" className={classes.warningText}>
        <FormattedText id="settings.messageLanguage.templatesWarning" values={{ language: englishName(language) }} />{' '}
        <Link to="/dashboard/settings?tab=templates" className={classes.link}>
          <FormattedText id="settings.messageLanguage.goToTemplates" />
        </Link>
      </Typography>
    </div>
  )
}

function ScopeNote() {
  return (
    <Typography variant="body" className={classes.scopeNote}>
      <FormattedMessage
        id="settings.messageLanguage.scopeNote"
        values={{
          link: (chunks: ReactNode) => (
            <Link to="/dashboard/settings?tab=calendars" className={classes.link}>
              {chunks}
            </Link>
          ),
        }}
      />
    </Typography>
  )
}

function MessageLanguageFooter({ isSaving, onCancel, onSave }: MessageLanguageFooterProps) {
  return (
    <div className={classes.footer}>
      <Typography variant="body" className={classes.appliesNote}>
        <FormattedText id="settings.messageLanguage.appliesAfterSave" />
      </Typography>
      <div className={classes.actionButtons}>
        <Button type="button" variant="withBorder" widthType="content" onClick={onCancel} disabled={isSaving}>
          <FormattedText id="settings.messageLanguage.cancel" />
        </Button>
        <Button type="button" widthType="content" onClick={onSave} isLoading={isSaving} disabled={isSaving}>
          <FormattedText id="settings.messageLanguage.save" />
        </Button>
      </div>
    </div>
  )
}

function useAutomaticSourceText(countryCode: string | null): string {
  const { formatMessage } = useIntl()
  const country = formatCountryName(countryCode)
  return country
    ? formatMessage({ id: 'settings.messageLanguage.basedOnCountry' }, { country })
    : formatMessage({ id: 'settings.messageLanguage.noCountry' })
}

export function ClientMessageLanguageCard({ className }: ClientMessageLanguageCardProps) {
  const { formatMessage } = useIntl()
  const card = useClientMessageLanguageCard()
  const automaticSourceText = useAutomaticSourceText(card.countryCode)
  if (!card.profile) return null

  return (
    <section
      id={MESSAGE_LANGUAGE_ANCHOR_ID}
      className={classNames(classes.card, className)}
      aria-label={formatMessage({ id: 'settings.messageLanguage.title' })}
      data-testid="client-message-language-card"
    >
      <Typography variant="h5" className={classes.title}>
        <FormattedText id="settings.messageLanguage.title" />
      </Typography>
      <Typography variant="body" className={classes.description}>
        <FormattedText id="settings.messageLanguage.description" />
      </Typography>
      <MessageLanguageSelect
        value={card.selected}
        automaticLanguage={card.automatic.language}
        automaticSourceText={automaticSourceText}
        onChange={card.stage}
        disabled={card.isSaving}
      />
      <MessageLanguageDetails
        saved={card.saved}
        selected={card.selected}
        isStaged={card.isStaged}
        automaticLanguage={card.automatic.language}
        automaticSourceText={automaticSourceText}
      />
      {card.isStaged && (
        <StagedChange
          language={card.effectiveLanguage}
          businessName={card.businessName}
          confirmationsEnabled={card.confirmationsEnabled}
        />
      )}
      <ScopeNote />
      {card.isStaged && (
        <MessageLanguageFooter isSaving={card.isSaving} onCancel={card.cancel} onSave={() => void card.save()} />
      )}
    </section>
  )
}
